import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createFolder, createList, updateList, deleteList, duplicateList, updateFolder, deleteFolder, duplicateFolderWithLists, saveAsTemplate, getTemplates, createProjectFromTemplate, deleteTemplate, createProject, getProjectMembers, setProjectMemberRole } from '../services/firestore';
import toast from 'react-hot-toast';
import { useI18n } from '../../../contexts/I18nContext';
import ListContextMenu from './ListContextMenu';
import IconSelector, { AVAILABLE_ICONS } from './IconSelector';
import {
  Home,
  Inbox,
  Reply,
  CheckSquare,
  Target,
  MoreHorizontal,
  Plus,
  Folder,
  List,
  Settings,
  LogOut,
  ChevronDown,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  X,
  Loader,
  MoreVertical,
  ChevronRight,
  Copy,
  FileText,
  Trash2,
  Users,
  Shield,
} from 'lucide-react';

const SidebarNew = ({ width, setWidth, onClose, collapsed, onToggleCollapse }) => {
  const { user } = useAuth();
  const {
    projects = [],
    folders = [],
    lists = [],
    selectedProjectId,
    setSelectedProjectId,
    selectedFolderId,
    setSelectedFolderId,
    selectedListId,
    setSelectedListId,
    reloadFolders,
    reloadLists,
    activeView,
    setActiveView,
    users: workspaceUsers = [],
  } = useTask();
  const { t } = useI18n();
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedFolders, setExpandedFolders] = useState({});

  // Expandir proyectos por defecto al cargar
  useEffect(() => {
    if (projects.length > 0 && Object.keys(expandedProjects).length === 0) {
      const allExpanded = {};
      projects.forEach(p => allExpanded[p.id] = true);
      setExpandedProjects(allExpanded);
    }
  }, [projects]);

  // Expandir carpetas por defecto al cargar
  useEffect(() => {
    if (folders.length > 0 && Object.keys(expandedFolders).length === 0) {
      const allExpanded = {};
      folders.forEach(f => allExpanded[f.id] = true);
      setExpandedFolders(allExpanded);
    }
  }, [folders]);

  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose && window.innerWidth < 768) onClose();
  };

  // Estados para crear carpetas y listas
  const [creatingFolderId, setCreatingFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingListFolderId, setCreatingListFolderId] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [loadingCreate, setLoadingCreate] = useState(false);

  // Estados para menú contextual
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [renamingListId, setRenamingListId] = useState(null);
  const [renamingListName, setRenamingListName] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renamingFolderName, setRenamingFolderName] = useState('');
  const [showIconSelector, setShowIconSelector] = useState(false);
  const [iconSelectorTarget, setIconSelectorTarget] = useState(null); // 'list' o 'folder'
  const [loadingAction, setLoadingAction] = useState(false);
  const [contextMenuType, setContextMenuType] = useState(null); // 'list' o 'folder'

  // Template states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [showNewProject, setShowNewProject] = useState(false);

  // Reorder lists with arrows
  const handleReorderList = async (allLists, index, direction, projectId) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= allLists.length) return;
    const reordered = [...allLists];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    try {
      await Promise.all(reordered.map((list, i) =>
        updateList(user.uid, list._projectId, list._folderId, list.id, { orden: i })
      ));
      if (reloadLists) await reloadLists();
    } catch { toast.error('Error al reordenar'); }
  };

  // Member management states
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [membersProjectId, setMembersProjectId] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const handleShowMembers = async (projectId) => {
    setMembersProjectId(projectId);
    setShowMembersModal(true);
    setLoadingMembers(true);
    try {
      const members = await getProjectMembers(user.uid, projectId);
      setProjectMembers(members);
    } catch { setProjectMembers([]); }
    finally { setLoadingMembers(false); }
  };

  const handleChangeRole = async (targetUserId, newRole) => {
    try {
      await setProjectMemberRole(user.uid, membersProjectId, targetUserId, newRole);
      setProjectMembers(prev => prev.map(m => m.userId === targetUserId ? { ...m, role: newRole } : m));
      toast.success('Rol actualizado');
    } catch { toast.error('Error al cambiar rol'); }
  };

  const getMemberInfo = (userId) => {
    return workspaceUsers.find(u => u.id === userId || u.uid === userId) || { displayName: userId, email: '' };
  };

  const handleSaveAsTemplate = async (projectId) => {
    try {
      await saveAsTemplate(user.uid, projectId);
      toast.success('Plantilla guardada');
    } catch { toast.error('Error al guardar plantilla'); }
  };

  const handleShowTemplates = async () => {
    const t = await getTemplates(user.uid);
    setTemplates(t);
    setShowTemplateModal(true);
  };

  const handleCreateFromTemplate = async (templateId) => {
    const name = prompt('Nombre del proyecto:');
    if (!name) return;
    try {
      const projectId = await createProjectFromTemplate(user.uid, templateId, name);
      toast.success('Proyecto creado desde plantilla');
      setShowTemplateModal(false);
      navigate(`/tareas/espacio/${projectId}`);
    } catch { toast.error('Error al crear proyecto'); }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Eliminar esta plantilla?')) return;
    try {
      await deleteTemplate(user.uid, templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Plantilla eliminada');
    } catch { toast.error('Error'); }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const projectId = await createProject(user.uid, { nombre: newProjectName.trim() });
      toast.success('Espacio creado');
      setNewProjectName('');
      setShowNewProject(false);
      navigate(`/tareas/espacio/${projectId}`);
    } catch { toast.error('Error al crear espacio'); }
  };

  // Resize Logic
  const handleMouseDown = (e) => {
    e.preventDefault();
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const newWidth = e.clientX;
    if (newWidth > 200 && newWidth < 600) {
      setWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const toggleProjectExpanded = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const toggleFolderExpanded = (folderId) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const getProjectColor = (projectId) => {
    const colors = ['bg-purple-500', 'bg-blue-500', 'bg-pink-500', 'bg-green-500', 'bg-orange-500'];
    const index = projectId?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const handleCreateFolder = async (projectId) => {
    if (!newFolderName.trim() || !user?.uid) return;

    setLoadingCreate(true);
    try {
      await createFolder(user.uid, projectId, { nombre: newFolderName.trim() });
      setNewFolderName('');
      setCreatingFolderId(null);
      // Recargar carpetas
      if (reloadFolders) await reloadFolders();
    } catch (error) {
      console.error('Error al crear carpeta:', error);
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleCreateList = async (projectId, folderId) => {
    if (!newListName.trim() || !user?.uid) return;

    setLoadingCreate(true);
    try {
      await createList(user.uid, projectId, folderId, { nombre: newListName.trim(), icon: 'List' });
      setNewListName('');
      setCreatingListFolderId(null);
      // Recargar listas
      if (reloadLists) await reloadLists();
    } catch (error) {
      console.error('Error al crear lista:', error);
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleRenameList = async () => {
    if (!renamingListName.trim() || !selectedList || !user?.uid) return;
    setLoadingAction(true);
    try {
      await updateList(user.uid, selectedList.projectId, selectedList.folderId, selectedList.id, { nombre: renamingListName.trim() });
      setRenamingListId(null);
      setRenamingListName('');
      if (reloadLists) await reloadLists();
    } catch (error) {
      console.error('Error al renombrar lista:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleChangeIcon = async (iconId) => {
    if (!selectedList || !user?.uid) return;
    setLoadingAction(true);
    try {
      await updateList(user.uid, selectedList.projectId, selectedList.folderId, selectedList.id, { icon: iconId });
      if (reloadLists) await reloadLists();
    } catch (error) {
      console.error('Error al cambiar icono:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleMoveList = async (targetFolderId) => {
    if (!selectedList || !user?.uid) return;
    setLoadingAction(true);
    try {
      await duplicateList(user.uid, selectedList.projectId, selectedList.folderId, selectedList.id, targetFolderId);
      await deleteList(user.uid, selectedList.projectId, selectedList.folderId, selectedList.id);
      if (reloadLists) await reloadLists();
    } catch (error) {
      console.error('Error al mover lista:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDuplicateList = async () => {
    if (!selectedList || !user?.uid) return;
    setLoadingAction(true);
    try {
      await duplicateList(user.uid, selectedList.projectId, selectedList.folderId, selectedList.id, selectedList.folderId);
      if (reloadLists) await reloadLists();
    } catch (error) {
      console.error('Error al duplicar lista:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleArchiveList = async () => {
    if (!selectedList || !user?.uid) return;
    setLoadingAction(true);
    try {
      await updateList(user.uid, selectedList.projectId, selectedList.folderId, selectedList.id, { archived: true });
      if (reloadLists) await reloadLists();
    } catch (error) {
      console.error('Error al archivar lista:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteList = async () => {
    if (!selectedList || !user?.uid) return;
    setLoadingAction(true);
    try {
      await deleteList(user.uid, selectedList.projectId, selectedList.folderId, selectedList.id);
      if (reloadLists) await reloadLists();
    } catch (error) {
      console.error('Error al eliminar lista:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const getListIcon = (iconId) => {
    const iconObj = AVAILABLE_ICONS.find(i => i.id === iconId);
    return iconObj?.icon || List;
  };

  // Handlers para carpetas
  const handleRenameFolder = async () => {
    if (!renamingFolderName.trim() || !selectedFolder || !user?.uid) return;
    setLoadingAction(true);
    try {
      await updateFolder(user.uid, selectedFolder.projectId, selectedFolder.id, { nombre: renamingFolderName.trim() });
      setRenamingFolderId(null);
      setRenamingFolderName('');
      if (reloadFolders) await reloadFolders();
    } catch (error) {
      console.error('Error al renombrar carpeta:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleChangeIconFolder = async (iconId) => {
    if (!selectedFolder || !user?.uid) return;
    setLoadingAction(true);
    try {
      await updateFolder(user.uid, selectedFolder.projectId, selectedFolder.id, { icon: iconId });
      if (reloadFolders) await reloadFolders();
    } catch (error) {
      console.error('Error al cambiar icono de carpeta:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleMoveFolder = async (targetProjectId) => {
    if (!selectedFolder || !user?.uid) return;
    setLoadingAction(true);
    try {
      // Duplicar en proyecto destino
      await duplicateFolderWithLists(user.uid, selectedFolder.projectId, selectedFolder.id, targetProjectId);
      // Eliminar de proyecto origen
      await deleteFolder(user.uid, selectedFolder.projectId, selectedFolder.id);
      if (reloadFolders) await reloadFolders();
    } catch (error) {
      console.error('Error al mover carpeta:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDuplicateFolder = async () => {
    if (!selectedFolder || !user?.uid) return;
    setLoadingAction(true);
    try {
      await duplicateFolderWithLists(user.uid, selectedFolder.projectId, selectedFolder.id);
      if (reloadFolders) await reloadFolders();
    } catch (error) {
      console.error('Error al duplicar carpeta:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleArchiveFolder = async () => {
    if (!selectedFolder || !user?.uid) return;
    setLoadingAction(true);
    try {
      await updateFolder(user.uid, selectedFolder.projectId, selectedFolder.id, { archived: true });
      if (reloadFolders) await reloadFolders();
    } catch (error) {
      console.error('Error al archivar carpeta:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!selectedFolder || !user?.uid) return;
    setLoadingAction(true);
    try {
      await deleteFolder(user.uid, selectedFolder.projectId, selectedFolder.id);
      if (reloadFolders) await reloadFolders();
    } catch (error) {
      console.error('Error al eliminar carpeta:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  // ── Collapsed sidebar (icon rail) ──
  if (collapsed) {
    const navItems = [
      { path: '/tareas', icon: Home, label: t('nav.home') },
      { path: '/tareas/inbox', icon: Inbox, label: t('nav.inbox') },
      { path: '/tareas/mis-tareas', icon: CheckSquare, label: t('nav.myTasks') },
      { path: '/tareas/metas', icon: Target, label: t('nav.goals') },
    ];
    return (
      <aside className="w-16 bg-[#f3f4f6] border-r border-gray-200 flex flex-col h-full items-center py-3 gap-0.5 flex-shrink-0">
        <button onClick={onToggleCollapse} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors mb-2" title="Expandir sidebar">
          <ChevronsRight className="w-5 h-5" />
        </button>
        {navItems.map(item => {
          const isActive = location.pathname === item.path || (item.path === '/tareas' && location.pathname === '/tareas/');
          return (
            <button key={item.path} onClick={() => handleNavigation(item.path)} title={item.label}
              className={`p-2.5 rounded-lg transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'}`}>
              <item.icon className="w-5 h-5" />
            </button>
          );
        })}
        <div className="w-8 border-t border-gray-300 my-2" />
        <div className="flex-1 overflow-y-auto flex flex-col items-center gap-1 w-full px-1.5 scrollbar-hide">
          {projects.map((project, idx) => {
            const colors = ['bg-blue-500', 'bg-purple-500', 'bg-teal-500', 'bg-amber-500', 'bg-rose-500'];
            const isActive = selectedProjectId === project.id;
            return (
              <button key={project.id}
                onClick={() => {
                  setSelectedProjectId(project.id);
                  const projectFolders = folders.filter(f => f.projectId === project.id);
                  if (projectFolders.length > 0) {
                    setSelectedFolderId(projectFolders[0].id);
                    const projectLists = lists.filter(l => l.folderId === projectFolders[0].id);
                    if (projectLists.length > 0) {
                      setSelectedListId(projectLists[0].id);
                      handleNavigation(`/tareas/lista/${projectLists[0].id}`);
                    }
                  }
                }}
                title={project.nombre || project.name}
                className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                  isActive ? `${colors[idx % colors.length]} text-white ring-2 ring-offset-1 ring-blue-400` : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}>
                {(project.nombre || project.name || 'P').charAt(0).toUpperCase()}
              </button>
            );
          })}
        </div>
        <div className="border-t border-gray-300 pt-2 flex flex-col items-center gap-0.5">
          <button onClick={() => handleNavigation('/tareas/ajustes')} className="p-2.5 text-gray-400 hover:bg-gray-200 rounded-lg transition-colors" title="Configuración">
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </aside>
    );
  }

  // ── Expanded sidebar ──
  return (
    <aside
      style={{ width: window.innerWidth < 768 ? '280px' : width }}
      className="bg-[#f8f9fa] border-r border-gray-200 p-4 flex flex-col overflow-y-auto h-full relative flex-shrink-0"
    >
      {/* Resize Handle - hidden on mobile */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 transition-colors z-50 hidden md:block"
        onMouseDown={handleMouseDown}
      />
      {/* Header — la marca y los accesos Inicio/Bandeja/Mis tareas/Metas viven ahora
          en el sidebar global (components/layout/AppSidebar.jsx); aquí solo queda
          el árbol de espacios del módulo. */}
      <div className="flex items-center mb-2">
        <span className="text-sm font-semibold text-gray-800 flex-1">Tareas</span>
        <button onClick={onToggleCollapse} className="hidden md:flex p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Comprimir panel">
          <ChevronsLeft className="w-4 h-4" />
        </button>
        {onClose && (
          <button onClick={onClose} className="md:hidden p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Espacios / Proyectos */}
      <div className="flex-1 overflow-y-auto mt-2 mb-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase px-3 mb-3 flex justify-between items-center">
          {t('nav.spaces')}
          <div className="flex items-center gap-1">
            <button onClick={handleShowTemplates} className="p-0.5 text-gray-400 hover:text-gray-700 transition" title="Plantillas">
              <FileText className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setShowNewProject(true)} className="p-0.5 text-gray-400 hover:text-gray-700 transition" title="Nuevo espacio">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </h3>

        {/* Crear nuevo proyecto */}
        {showNewProject && (
          <div className="mx-3 mb-3 flex gap-1">
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateProject(); if (e.key === 'Escape') setShowNewProject(false); }}
              placeholder="Nombre del espacio..."
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              autoFocus
            />
            <button onClick={handleCreateProject} className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600">
              <Plus className="w-4 h-4" />
            </button>
            <button onClick={() => { setShowNewProject(false); setNewProjectName(''); }} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {projects && projects.length > 0 ? (
          <ul className="space-y-1">
            {projects.map((project) => {
              const isProjectSelected = selectedProjectId === project.id;
              const isExpanded = expandedProjects[project.id];
              const projectFolders = folders.filter((f) => f.projectId === project.id);

              return (
                <li key={project.id}>
                  <div
                    className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition group ${isProjectSelected ? 'bg-gray-100 text-gray-800 font-medium' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                  >
                    <span
                      className="flex items-center space-x-3 flex-1"
                      onClick={() => {
                        setSelectedProjectId(project.id);
                        handleNavigation(`/tareas/espacio/${project.id}`);
                      }}
                    >
                      <span className={`w-5 h-5 rounded text-white flex items-center justify-center text-xs font-bold ${getProjectColor(project.id)}`}>
                        {project.nombre?.[0]?.toUpperCase() || 'P'}
                      </span>
                      <span className="text-sm">{project.nombre}</span>
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleShowMembers(project.id); }}
                        className="p-0.5 opacity-0 group-hover:opacity-100 transition text-gray-400 hover:text-blue-600"
                        title="Miembros y permisos"
                      >
                        <Users className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSaveAsTemplate(project.id); }}
                        className="p-0.5 opacity-0 group-hover:opacity-100 transition text-gray-400 hover:text-amber-600"
                        title="Guardar como plantilla"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      {isExpanded && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Create list in the first folder of this project (auto-select folder)
                            const defaultFolder = projectFolders[0];
                            if (defaultFolder) {
                              setCreatingListFolderId(defaultFolder.id);
                            } else {
                              // No folders yet - create one first
                              setCreatingFolderId(project.id);
                            }
                          }}
                          className="p-0.5 opacity-0 group-hover:opacity-100 transition"
                          title="Crear lista"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      )}
                      {/* Count of lists, not folders */}
                      <span className="text-xs text-gray-400">
                        {lists.filter(l => projectFolders.some(f => f.id === l.folderId)).length}
                      </span>
                      <ChevronRight
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleProjectExpanded(project.id);
                        }}
                        className={`w-4 h-4 transition-transform cursor-pointer text-gray-400 hover:text-gray-600 ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </div>

                  {/* Crear carpeta */}
                  {isExpanded && creatingFolderId === project.id && (
                    <div className="pl-8 mt-2 flex gap-1">
                      <input
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreateFolder(project.id);
                        }}
                        placeholder="Nueva carpeta..."
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                        autoFocus
                      />
                      <button
                        onClick={() => handleCreateFolder(project.id)}
                        disabled={loadingCreate}
                        className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                      >
                        {loadingCreate ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => {
                          setCreatingFolderId(null);
                          setNewFolderName('');
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Listas (aplanadas - sin mostrar carpetas intermedias) */}
                  {isExpanded && (() => {
                    // Flatten: collect all lists from all folders under this project
                    const allProjectLists = projectFolders.flatMap(folder =>
                      lists.filter(l => l.folderId === folder.id).map(l => ({ ...l, _folderId: folder.id, _projectId: project.id }))
                    );

                    return (
                      <>
                        {/* Crear lista (usa la primera carpeta del proyecto) */}
                        {creatingListFolderId && projectFolders.some(f => f.id === creatingListFolderId) && (
                          <div className="pl-10 mt-2 flex gap-1">
                            <input
                              type="text"
                              value={newListName}
                              onChange={(e) => setNewListName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreateList(project.id, creatingListFolderId);
                              }}
                              placeholder="Nueva lista..."
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                              autoFocus
                            />
                            <button
                              onClick={() => handleCreateList(project.id, creatingListFolderId)}
                              disabled={loadingCreate}
                              className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                            >
                              {loadingCreate ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => { setCreatingListFolderId(null); setNewListName(''); }}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {allProjectLists.length > 0 && (
                          <ul className="pl-6 mt-1 space-y-0.5">
                            {(() => {
                              const sortedLists = allProjectLists.sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));
                              return sortedLists.map((list, listIndex) => {
                              const ListIcon = getListIcon(list.icon);
                              const isListRenaming = renamingListId === list.id;

                              return (
                                <li
                                  key={list.id}
                                  className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition text-sm group ${selectedListId === list.id
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                  <span
                                    className="flex items-center space-x-2 flex-1 min-w-0"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (list.id) {
                                        setSelectedFolderId(list._folderId);
                                        setSelectedListId(list.id);
                                        handleNavigation(`/tareas/lista/${list.id}`);
                                      }
                                    }}
                                  >
                                    <ListIcon className="w-4 h-4 flex-shrink-0" />
                                    {isListRenaming ? (
                                      <input
                                        type="text"
                                        value={renamingListName}
                                        onChange={(e) => setRenamingListName(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleRenameList();
                                          if (e.key === 'Escape') setRenamingListId(null);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex-1 px-1 py-0 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                        autoFocus
                                      />
                                    ) : (
                                      <span className="truncate">{list.nombre}</span>
                                    )}
                                  </span>

                                  {/* Reorder arrows */}
                                  <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                                    {listIndex > 0 && (
                                      <button onClick={(e) => { e.stopPropagation(); handleReorderList(sortedLists, listIndex, -1, project.id); }}
                                        className="p-0.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Subir">
                                        <ChevronUp className="w-3 h-3" />
                                      </button>
                                    )}
                                    {listIndex < sortedLists.length - 1 && (
                                      <button onClick={(e) => { e.stopPropagation(); handleReorderList(sortedLists, listIndex, 1, project.id); }}
                                        className="p-0.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Bajar">
                                        <ChevronDown className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                  {/* Botón menú (3 puntos) */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedList({ ...list, projectId: list._projectId, folderId: list._folderId });
                                      setContextMenu({
                                        top: e.currentTarget.getBoundingClientRect().bottom,
                                        left: e.currentTarget.getBoundingClientRect().right
                                      });
                                      setContextMenuType('list');
                                    }}
                                    className="p-0.5 opacity-0 group-hover:opacity-100 transition flex-shrink-0"
                                  >
                                    <MoreVertical className="w-3.5 h-3.5" />
                                  </button>
                                </li>
                              );
                            });
                            })()}
                          </ul>
                        )}
                      </>
                    );
                  })()}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-xs text-gray-400 px-3">No hay espacios</p>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto border-t pt-4 space-y-2">
        <button className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-100 w-full transition text-sm">
          <Settings className="w-5 h-5" />
          <span>Personalizar la barra lateral</span>
        </button>
        <button className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-100 w-full transition text-sm">
          <LogOut className="w-5 h-5" />
          <span>Salir</span>
        </button>
      </div>

      {/* Menú contextual de lista */}
      {contextMenu && contextMenuType === 'list' && selectedList && (
        <ListContextMenu
          list={selectedList}
          position={contextMenu}
          onClose={() => {
            setContextMenu(null);
            setContextMenuType(null);
          }}
          onRename={() => {
            setRenamingListId(selectedList.id);
            setRenamingListName(selectedList.nombre);
          }}
          onChangeIcon={() => {
            setIconSelectorTarget('list');
            setShowIconSelector(true);
          }}
          onMove={(targetFolderId) => handleMoveList(targetFolderId)}
          onDuplicate={() => handleDuplicateList()}
          onArchive={() => handleArchiveList()}
          onDelete={() => handleDeleteList()}
          folders={folders.filter(f => f.projectId === selectedList.projectId)}
          currentFolderId={selectedList.folderId}
        />
      )}

      {/* Menú contextual de carpeta */}
      {contextMenu && contextMenuType === 'folder' && selectedFolder && (
        <ListContextMenu
          list={selectedFolder}
          position={contextMenu}
          onClose={() => {
            setContextMenu(null);
            setContextMenuType(null);
          }}
          onRename={() => {
            setRenamingFolderId(selectedFolder.id);
            setRenamingFolderName(selectedFolder.nombre);
          }}
          onChangeIcon={() => {
            setIconSelectorTarget('folder');
            setShowIconSelector(true);
          }}
          onMove={(targetProjectId) => handleMoveFolder(targetProjectId)}
          onDuplicate={() => handleDuplicateFolder()}
          onArchive={() => handleArchiveFolder()}
          onDelete={() => handleDeleteFolder()}
          folders={projects}
          currentFolderId={selectedFolder.projectId}
          isFolderMenu={true}
        />
      )}

      {/* Selector de iconos */}
      {showIconSelector && (
        <IconSelector
          currentIcon={
            iconSelectorTarget === 'list'
              ? selectedList?.icon || 'List'
              : selectedFolder?.icon || 'Folder'
          }
          onSelectIcon={(iconId) => {
            if (iconSelectorTarget === 'list') {
              handleChangeIcon(iconId);
            } else {
              handleChangeIconFolder(iconId);
            }
          }}
          onClose={() => {
            setShowIconSelector(false);
            setIconSelectorTarget(null);
          }}
        />
      )}

      {/* Members Modal */}
      {showMembersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowMembersModal(false)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-500" />
                <h2 className="font-bold text-gray-800 dark:text-gray-100 text-sm">Miembros y Permisos</h2>
              </div>
              <button onClick={() => setShowMembersModal(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loadingMembers ? (
                <div className="flex justify-center py-8">
                  <Loader className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : projectMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No hay miembros en este proyecto</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {projectMembers.map(member => {
                    const info = getMemberInfo(member.userId);
                    const displayName = info.displayName || info.name || info.email || member.userId;
                    const initial = (displayName[0] || '?').toUpperCase();
                    return (
                      <div key={member.userId} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3 min-w-0">
                          {info.photoURL ? (
                            <img src={info.photoURL} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{initial}</div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{displayName}</p>
                            {info.email && <p className="text-xs text-gray-500 truncate">{info.email}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {member.isOwner ? (
                            <span className="px-2 py-1 text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full">Propietario</span>
                          ) : (
                            <select
                              value={member.role}
                              onChange={(e) => handleChangeRole(member.userId, e.target.value)}
                              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:border-blue-500"
                            >
                              <option value="admin">Admin</option>
                              <option value="editor">Editor</option>
                              <option value="viewer">Viewer</option>
                            </select>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800">
              <div className="text-[10px] text-gray-400 space-y-0.5">
                <p><strong>Admin:</strong> Control total del proyecto</p>
                <p><strong>Editor:</strong> Crear y editar tareas</p>
                <p><strong>Viewer:</strong> Solo lectura</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowTemplateModal(false)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-500" />
                <h2 className="font-bold text-gray-800 dark:text-gray-100 text-sm">Plantillas</h2>
              </div>
              <button onClick={() => setShowTemplateModal(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {templates.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No hay plantillas guardadas</p>
                  <p className="text-xs text-gray-400 mt-1">Guarda un proyecto como plantilla desde el icono de copiar</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t.name}</p>
                        <p className="text-xs text-gray-500">{t.folders?.length || 0} carpetas</p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => handleCreateFromTemplate(t.id)} className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 font-medium">
                          Usar
                        </button>
                        <button onClick={() => handleDeleteTemplate(t.id)} className="p-1 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default SidebarNew;
